// src/components/rodeoAi/ChatMessage.jsx
import React from 'react';
import { Box, Paper, Typography, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownComponents } from './markdownComponents';

const ChatMessage = ({ message, isStreaming, onCopy }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isUser = message.role === 'user';

    const handleCopy = () => {
        navigator.clipboard.writeText(message.content);
        if (onCopy) onCopy();
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                mb: { xs: 1.5, sm: 2 },
                animation: 'fadeIn 0.3s ease-in'
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    maxWidth: { xs: '95%', sm: '85%', md: '80%' },
                    px: { xs: 1.5, sm: 2, md: 2.5 },
                    py: { xs: 1.25, sm: 1.5 },
                    position: 'relative',
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'dark'
                            ? isUser
                                ? 'rgba(0, 127, 255, 0.15)'
                                : 'rgba(19, 47, 76, 0.6)'
                            : isUser
                                ? 'rgba(0, 127, 255, 0.1)'
                                : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: (theme) =>
                        theme.palette.mode === 'dark'
                            ? isUser
                                ? '1px solid rgba(0, 127, 255, 0.3)'
                                : '1px solid rgba(255, 255, 255, 0.1)'
                            : isUser
                                ? '1px solid rgba(0, 127, 255, 0.2)'
                                : '1px solid rgba(0, 0, 0, 0.08)',
                    boxShadow: (theme) =>
                        theme.palette.mode === 'dark'
                            ? '0 4px 24px rgba(0,0,0,0.2)'
                            : '0 2px 12px rgba(0,0,0,0.05)',
                    borderRadius: { xs: 1.5, sm: 2 },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        transform: isMobile ? 'none' : 'translateY(-2px)',
                        boxShadow: (theme) =>
                            theme.palette.mode === 'dark'
                                ? '0 8px 32px rgba(0,0,0,0.3)'
                                : '0 4px 16px rgba(0,0,0,0.1)',
                        '& .copy-button': {
                            opacity: 1
                        }
                    }
                }}
            >
                {/* Copy Button - Only for AI messages */}
                {!isUser && (
                    <Tooltip title="Copy message">
                        <IconButton
                            className="copy-button"
                            size="small"
                            onClick={handleCopy}
                            sx={{
                                position: 'absolute',
                                top: { xs: 4, sm: 8 },
                                right: { xs: 4, sm: 8 },
                                opacity: isMobile ? 0.7 : 0,
                                transition: 'opacity 0.2s',
                                width: { xs: 28, sm: 32 },
                                height: { xs: 28, sm: 32 },
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.1)'
                                        : 'rgba(0,0,0,0.05)',
                                '&:hover': {
                                    backgroundColor: (theme) =>
                                        theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.15)'
                                            : 'rgba(0,0,0,0.1)'
                                },
                                '&:active': {
                                    transform: 'scale(0.95)'
                                }
                            }}
                        >
                            <ContentCopyIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                        </IconButton>
                    </Tooltip>
                )}

                {/* Message Content */}
                {isUser ? (
                    <Typography
                        variant="body1"
                        sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            lineHeight: 1.5
                        }}
                    >
                        {message.content}
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            '& > *:first-of-type': {
                                mt: 0
                            },
                            '& > *:last-child': {
                                mb: 0
                            },
                            // Adjust font sizes for mobile
                            '& p': {
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                            },
                            '& h1': {
                                fontSize: { xs: '1.5rem', sm: '2rem' }
                            },
                            '& h2': {
                                fontSize: { xs: '1.25rem', sm: '1.5rem' }
                            },
                            '& h3': {
                                fontSize: { xs: '1.125rem', sm: '1.25rem' }
                            },
                            '& code': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            },
                            '& li': {
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                            },
                            // Ensure tables are scrollable on mobile
                            '& .MuiTableContainer-root': {
                                overflowX: 'auto',
                                WebkitOverflowScrolling: 'touch'
                            }
                        }}
                    >
                        <ReactMarkdown
                            components={markdownComponents}
                            remarkPlugins={[remarkGfm]}
                        >
                            {message.content + (isStreaming ? ' ▊' : '')}
                        </ReactMarkdown>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default ChatMessage;